/* ============================================================
   CATCH-UP — graceful live re-sync, as PURE decisions.
   ------------------------------------------------------------
   When the picture stalls it falls BEHIND the live broadcast line.
   Rather than a jarring forward SEEK over program content, we carry
   an honest playback lag (how far behind real-live we currently are)
   and reclaim it inside AD breaks: the last 20% of every ad is slack
   we can drop to move closer to live. Self-limiting by construction —
   ≥80% of every ad always plays, and we can never pass live. The
   schedule (liveSlot) and the LIVE badge are untouched; we only ever
   nudge the player's PLAYBACK toward the line.
   ============================================================ */

/** Fraction of each ad reserved for reconciliation (its last 20%). */
export const AD_RECLAIM_FRACTION = 0.2

/** Hard runaway backstop: past this much lag, give up the graceful path and take ONE real
 *  corrective seek to real-live (better than sitting minutes behind forever). */
export const MAX_LIVE_LAG_S = 45

/** How much lag to reclaim while sitting on an ad. Reclaim only once the ad is in its last
 *  20% (`adOffset >= 0.8*adDur`), and at most `min(lag, 0.2*adDur)` — so ≥80% of the ad always
 *  plays, the player converges to live monotonically, and it can never overshoot (reclaim ≤ lag,
 *  and 0.2*adDur ≤ the ad's remaining time at the 80% mark). 0 ⇒ nothing to do this tick. */
export const adTailReclaim = (lag: number, adDurSec: number, adOffsetSec: number): number => {
  if (lag <= 0 || adDurSec <= 0) return 0
  if (adOffsetSec < adDurSec * (1 - AD_RECLAIM_FRACTION)) return 0 // not in the tail yet
  return Math.min(lag, adDurSec * AD_RECLAIM_FRACTION)
}
